import FileName from "./FileName";
import { useEffect, useRef, useState } from "react";
import Spinner from "../../../../Spinner";
import filterResponse from "../../../../../filterResponse";
import useMediaRetry from "../../../../../util/mediaRetry";

// Renditions, best-to-worst. None of them is guaranteed: they are produced by a transcoding
// pipeline that may not have run for a given file (or at all in this environment), so every one is
// probed and the uploaded file itself is always offered as the last resort. Assuming a rendition
// existed is what made the player show a black frame for every video.
const QUALITIES = ["1080p", "720p", "480p"];
const ORIGINAL = "original";
const PREFERRED_QUALITY = "720p";
const SPEEDS = [1, 1.5, 2];

function variantUrl(key, quality) {
    if (quality === ORIGINAL) {
        return process.env.REACT_APP_ENDPOINT + '/files/' + key;
    }
    return process.env.REACT_APP_ENDPOINT + '/files/' + key + '.' + quality;
}

// Which renditions exist, straight from the backend.
//
// This used to be worked out here by requesting each label and reading the status. That is one
// request per tier per video, and unreliable in a way that shows: a request failing for any reason —
// a timeout, a dropped connection, simply being queued behind the dozens of others a full page
// fires — is indistinguishable from "not there", so the same file offered different qualities on
// different page loads. One request, answered by the bucket, replaces the guessing.
//
// `track` asks for the renditions of one camera of a multi-camera file ("<key>.trackN.720p").
async function existingRenditions(key, signal, track = null) {
    const query = track === null ? '' : '?track=' + track;
    const response = await fetch(process.env.REACT_APP_ENDPOINT + '/files/' + key + '/renditions' + query, {
        credentials: "include",
        signal,
    });
    await filterResponse(response);
    if (!response.ok) {
        throw new Error("Could not list the renditions of " + key);
    }
    const { renditions } = await response.json();
    return Array.isArray(renditions) ? renditions : [];
}

// The video tracks of a recording that has several cameras in one file (an Apple Kepler capture
// holds a grayscale tracking-camera grid and a colour view). A browser only ever plays the first,
// so the backend serves any one of them on request (?track=N). Not knowing just means no choice is
// offered, which is how every ordinary video looks anyway.
async function videoTracks(key, signal) {
    try {
        const response = await fetch(process.env.REACT_APP_ENDPOINT + '/files/' + key + '/tracks', {
            credentials: "include",
            signal,
        });
        await filterResponse(response);
        if (!response.ok) {
            throw new Error("Could not list the video tracks of " + key);
        }
        const { tracks } = await response.json();
        return Array.isArray(tracks) ? tracks : [];
    }
    catch (e) {
        if (e.name !== "AbortError") {
            console.warn("Could not list the video tracks; offering the file as uploaded only.", e);
        }
        return [];
    }
}

const ALL_TRACKS = "all";

// Qualities offered for a list of renditions found: best-to-worst, then the original, which is
// always there.
function qualityList(found) {
    return [...QUALITIES.filter(q => found.includes(q)), ORIGINAL];
}

// The quality to show from `options`: the one picked if it is there, else the preferred one.
function pickQuality(options, quality) {
    if (options.includes(quality)) {
        return quality;
    }
    return options.includes(PREFERRED_QUALITY) ? PREFERRED_QUALITY : options[0];
}

// One camera of the file. Its own 720p/480p copy when the worker has made one: the original is
// over a gigabyte for a few seconds of a Kepler recording, far too much to stream for a preview.
// Without one, the original with the other cameras hidden (?track=N), streamed by the backend.
function trackSrc(key, track, quality = ORIGINAL) {
    if (quality !== ORIGINAL) {
        return variantUrl(key + '.track' + track, quality) + '?redirect=1';
    }
    return variantUrl(key, ORIGINAL) + '?redirect=1&track=' + track;
}

export function trackLabel(track) {
    const size = track.width && track.height ? ` · ${track.width}×${track.height}` : "";
    return `Track ${track.index + 1}${track.monochrome ? " · grayscale" : ""}${size}`;
}

// One player. Remembers where playback was so that a new source — another quality, another track,
// or a reload after a failed request — carries on from there instead of restarting.
function VideoPlayer({ videoRef: givenRef, playbackRef: givenPlayback, src, speed, startAt = 0, label }) {
    const ownRef = useRef();
    const videoRef = givenRef || ownRef;
    const ownPlayback = useRef({ time: startAt, playing: false });
    const playbackRef = givenPlayback || ownPlayback;
    const [failed, setFailed] = useState(false);

    // A reload lands in onLoadedMetadata below like a quality switch does, so it is told where
    // playback was in the same way.
    const playback = useMediaRetry(videoRef, src, {
        onGiveUp: () => setFailed(true),
        onBeforeRetry: (video) => {
            playbackRef.current = { time: video.currentTime, playing: !video.paused };
        },
    });

    useEffect(() => setFailed(false), [src]);

    function onLoadedMetadata() {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        if (playbackRef.current.time) {
            video.currentTime = playbackRef.current.time;
        }
        video.playbackRate = speed;
        if (playbackRef.current.playing) {
            video.play().catch(e => console.warn("Could not resume the video.", e));
        }
    }

    // Keep the native controls (which may reset the rate on load) in sync with our selection.
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = speed;
        }
    }, [speed, src, videoRef]);

    return (
        <div className={"mw-100"}>
            {label && <div className={"small text-muted"}>{label}</div>}

            <video ref={videoRef} controls onLoadedMetadata={onLoadedMetadata}
                onError={playback.onError} onCanPlay={playback.onCanPlay}
                className={"mw-100"} style={{ maxHeight: "150px", width: "auto", height: "auto" }} src={src} />

            {failed &&
                <div className={"small text-danger"}>
                    This video could not be loaded.{" "}
                    <button type={"button"} className={"btn btn-link btn-sm p-0 align-baseline"}
                        onClick={() => { setFailed(false); playback.retryNow(); }}>Try again</button>
                </div>
            }
        </div>
    );
}

export default function PreviewVideoFile({ file, highlight }) {
    const videoRef = useRef();
    // Where playback was, so switching quality or track doesn't restart the video.
    const playbackRef = useRef({ time: 0, playing: false });

    const [available, setAvailable] = useState(null); // null while probing
    const [quality, setQuality] = useState(null);
    const [speed, setSpeed] = useState(1);
    const [tracks, setTracks] = useState([]);
    // null plays the file as uploaded — whichever track the browser picks, on the bucket's own
    // redirect. Nothing is picked for QA: another track is only shown when they ask for it.
    const [track, setTrack] = useState(null);
    // Each track's own renditions, by track index; a track not in here has none (yet).
    const [trackRenditions, setTrackRenditions] = useState({});

    useEffect(() => {
        const abortController = new AbortController();
        let isMounted = true;

        setTrack(null);
        setTracks([]);
        setTrackRenditions({});
        videoTracks(file.key, abortController.signal).then(async found => {
            if (!isMounted) {
                return;
            }
            setTracks(found);
            if (found.length < 2) {
                return;
            }
            // Not knowing just means that track plays from the original.
            const lists = await Promise.all(found.map(t => existingRenditions(file.key, abortController.signal, t.index)
                .catch(e => {
                    if (e.name !== "AbortError") {
                        console.warn("Could not list the renditions of track " + t.index + "; playing it from the original.", e);
                    }
                    return [];
                })));
            if (isMounted) {
                setTrackRenditions(Object.fromEntries(found.map((t, i) => [t.index, lists[i]])));
            }
        });

        (async () => {
            try {
                const found = await existingRenditions(file.key, abortController.signal);
                // Keep the best-to-worst order, then the original as the always-present fallback.
                const list = qualityList(found);

                if (isMounted) {
                    setAvailable(list);
                    setQuality(list.includes(PREFERRED_QUALITY) ? PREFERRED_QUALITY : list[0]);
                }
            }
            catch (e) {
                if (e.name === "AbortError") {
                    return;
                }
                // Nothing is known about the renditions, so offer only what is certain to be there.
                console.warn("Could not list the renditions; offering the original only.", e);
                if (isMounted) {
                    setAvailable([ORIGINAL]);
                    setQuality(ORIGINAL);
                }
            }
        })();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [file.key]);

    function rememberPlayback() {
        const video = videoRef.current;
        if (video) {
            playbackRef.current = { time: video.currentTime, playing: !video.paused };
        }
    }

    function changeQuality(newQuality) {
        if (newQuality === quality) {
            return;
        }
        rememberPlayback();
        setQuality(newQuality);
    }

    function changeTrack(newTrack) {
        if (newTrack === track) {
            return;
        }
        if (track !== ALL_TRACKS) {
            rememberPlayback();
        }
        setTrack(newTrack);
    }

    if (!quality) {
        return (
            <>
                <div className={"small text-break"}><FileName name={file.fileName} highlight={highlight} /></div>
                <Spinner small={true} />
            </>
        );
    }

    const choosesTrack = tracks.length > 1;
    const playsDefault = track === null || !choosesTrack;
    const qualitiesOf = index => qualityList(trackRenditions[index] || []);
    // The quality buttons on show: the file's own, one track's, or — side by side — every quality
    // some track has, each player falling back to its original where its track lacks it.
    const options = playsDefault ? available
        : track === ALL_TRACKS ? QUALITIES.filter(q => tracks.some(t => qualitiesOf(t.index).includes(q))).concat(ORIGINAL)
        : qualitiesOf(track);
    const shownQuality = pickQuality(options, quality);
    const src = playsDefault ? variantUrl(file.key, quality) + '?redirect=1'
        : trackSrc(file.key, track, shownQuality);

    return (
        <div className={"mw-100"}>
            <div className={"small text-break"}><FileName name={file.fileName} highlight={highlight} /></div>

            {choosesTrack && track === ALL_TRACKS
                ? <div className={"d-flex flex-wrap gap-2"}>
                    {tracks.map(t => (
                        <VideoPlayer key={t.index} speed={speed}
                            src={trackSrc(file.key, t.index, qualitiesOf(t.index).includes(shownQuality) ? shownQuality : ORIGINAL)}
                            startAt={playbackRef.current.time} label={trackLabel(t)} />
                    ))}
                </div>
                : <VideoPlayer videoRef={videoRef} playbackRef={playbackRef} src={src} speed={speed} />
            }

            <div className={"d-flex flex-wrap gap-2 mt-1"}>
                {choosesTrack &&
                    <div className={"btn-group btn-group-sm"} role={"group"} aria-label={"Video track"}>
                        {[null, ...tracks.map(t => t.index), ALL_TRACKS].map(option => (
                            <button key={String(option)} type={"button"}
                                title={option === null ? "The file as uploaded: the track the browser picks"
                                    : option === ALL_TRACKS ? "Every track side by side"
                                    : trackLabel(tracks[option])}
                                className={"btn " + (option === track ? "btn-secondary" : "btn-outline-secondary")}
                                onClick={() => changeTrack(option)}>
                                {option === null ? "Default" : option === ALL_TRACKS ? "All tracks"
                                    : `Track ${option + 1}${tracks[option].monochrome ? " (gray)" : ""}`}
                            </button>
                        ))}
                    </div>
                }

                {options.length > 1 &&
                    <div className={"btn-group btn-group-sm"} role={"group"} aria-label={"Video quality"}>
                        {options.map(q => (
                            <button key={q} type={"button"}
                                className={"btn " + (q === shownQuality ? "btn-secondary" : "btn-outline-secondary")}
                                onClick={() => changeQuality(q)}>
                                {q}
                            </button>
                        ))}
                    </div>
                }

                <div className={"btn-group btn-group-sm"} role={"group"} aria-label={"Playback speed"}>
                    {SPEEDS.map(s => (
                        <button key={s} type={"button"}
                            className={"btn " + (s === speed ? "btn-secondary" : "btn-outline-secondary")}
                            onClick={() => setSpeed(s)}>
                            {s}x
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
